import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, Fade } from "@mui/material";
import { useTranslation } from "react-i18next";
import liff from "@line/liff";
import { useNavigate } from "react-router-dom";
import { useBasicInfo } from "works/higurashi/hooks/useBasicInfo";

import Loading from "default/components/Loading";
import Header from "works/higurashi/components/Header";
import AppLayout from "works/higurashi/components/AppLayout";
import Error from "default/components/Error";
import TicketNotFound from "default/components/TicketNotFound";
import StyledButton from "default/components/StyledButton";
import useMyPageData from "works/higurashi/hooks/useMyPageData";

import { listHints } from "works/higurashi/utils/api";

const useHints = (accountKey, enabled = true) => {
  const [state, setState] = useState({
    hints: [],
    isLoading: enabled,
    isError: false,
  });

  const loadHints = useCallback(async () => {
    if (!enabled) {
      setState((prev) => ({ ...prev, isLoading: false, isError: false }));
      return;
    }

    if (!accountKey) {
      setState((prev) => ({ ...prev, isLoading: false }));
      return;
    }

    try {
      setState((prev) => ({ ...prev, isLoading: true, isError: false }));
      const hints = await listHints(accountKey);
      setState((prev) => ({ ...prev, hints, isError: false }));
    } catch (error) {
      console.error("Error loading hints:", error);
      setState((prev) => ({ ...prev, isError: true }));
    } finally {
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  }, [accountKey, enabled]);

  useEffect(() => {
    loadHints();
  }, [loadHints]);

  return state;
};

// Component for individual hint card
const HintCard = ({ hint, index, delay = 0 }) => {
  return (
    <Fade in timeout={800} style={{ transitionDelay: `${delay}ms` }}>
      <Box
        sx={{
          width: "76%",
          maxWidth: 600,
          borderRadius: 3,
          padding: 3,
          marginBottom: 5,
          boxShadow: "0 4px 20px rgba(255, 255, 255, 0.3)",
          border: "1px solid rgba(255, 255, 255, 0.2)",
          transition: "all 0.3s ease",
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.15)",
            boxShadow: "0 6px 25px rgba(255, 255, 255, 0.4)",
            transform: "translateY(-2px)",
          },
        }}
      >
        {/* Hint Name */}
        <Typography
          sx={(theme) => ({
            fontSize: "20px",
            marginBottom: 2,
            fontWeight: 800,
            textAlign: "center",
          })}
        >
          {hint.name}
        </Typography>

        {/* Hint Description */}
        <Typography
          sx={{
            fontSize: "14px",
            lineHeight: 1.8,
            textAlign: "left",
            padding: "0 8px",
            whiteSpace: "pre-line",
          }}
        >
          {hint.description}
        </Typography>
      </Box>
    </Fade>
  );
};

// Main Component
function Hints() {
  const navigate = useNavigate();
  const { work, chapter, accountKey, lng } = useBasicInfo();
  const { t } = useTranslation([work, "common"]);

  const {
    data: myPageData,
    isLoading: isMyPageLoading,
    isError: isMyPageError,
  } = useMyPageData(accountKey);
  const hasState = Boolean(myPageData);
  const { hints, isLoading, isError } = useHints(
    accountKey,
    !isMyPageLoading && hasState
  );

  const handleCloseWindow = () => {
    liff.closeWindow();
  };

  const handleNavigateHowToUse = () => {
    const queryParams = new URLSearchParams();
    if (accountKey) queryParams.set("account", accountKey);
    if (lng) queryParams.set("lng", lng);
    const queryString = queryParams.toString();
    navigate(
      `/${work || "higurashi"}/how-to-use${queryString ? `?${queryString}` : ""}`
    );
  };

  const handleNavigateQuests = () => {
    const queryParams = new URLSearchParams();
    if (accountKey) queryParams.set("account", accountKey);
    if (lng) queryParams.set("lng", lng);
    const queryString = queryParams.toString();
    navigate(
      `/${work || "higurashi"}/quests${queryString ? `?${queryString}` : ""}`
    );
  };

  const handleNavigateTickets = () => {
    const queryParams = new URLSearchParams();
    if (accountKey) queryParams.set("account", accountKey);
    if (lng) queryParams.set("lng", lng);
    const queryString = queryParams.toString();
    navigate(
      `/${work || "higurashi"}/tickets${queryString ? `?${queryString}` : ""}`
    );
  };

  // Render loading state
  if (isMyPageLoading || (hasState && isLoading)) {
    return (
      <AppLayout page="Hints" width="100%">
        <Loading />
      </AppLayout>
    );
  }

  // Render error state
  if (isMyPageError || (hasState && isError)) {
    return (
      <AppLayout page="Hints" width="100%">
        <Error />
      </AppLayout>
    );
  }

  if (!hasState) {
    return (
      <AppLayout page="Hints" width="100%">
        <TicketNotFound onClick={handleNavigateTickets} />
      </AppLayout>
    );
  }

  // Main render
  return (
    <AppLayout page="Hints" width="100%">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 5,
          width: "100%",
          mb: 5,
          minHeight: "100vh",
          paddingTop: 4,
          paddingBottom: 4,
        }}
      >
        {/* Header */}
        <Fade in timeout={600}>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Header
              title="Hints"
              highlightedWord="t"
              subtitle={t(`chapter.${chapter}`)}
              sx={{ marginBottom: 4 }}
            />
          </Box>
        </Fade>

        {/* Hints Section */}
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {/* Hint Cards */}
          {hints.map((hint, index) => (
            <HintCard
              key={hint.key}
              hint={hint}
              index={index}
              delay={400 + index * 200} // Staggered animation
            />
          ))}
        </Box>

        {/* チャットに戻る */}
        <Fade
          in
          timeout={400 + 200 * hints.length + 200}
          style={{ transitionDelay: "200ms" }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 4,
              width: "76%",
            }}
          >
            <StyledButton
              variant="contained"
              sx={{ maxWidth: 600 }}
              onClick={handleNavigateQuests}
            >
              {t("common:button.quests")}
            </StyledButton>

            <StyledButton
              variant="contained"
              sx={{ maxWidth: 600 }}
              onClick={handleNavigateHowToUse}
            >
              {t("common:Footer.how_to_play")}
            </StyledButton>

            <StyledButton
              variant="contained"
              sx={{ maxWidth: 600 }}
              onClick={handleCloseWindow}
            >
              {t("common:button.back_to_chat")}
            </StyledButton>
          </Box>
        </Fade>
      </Box>
    </AppLayout>
  );
}

export default Hints;
